import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { Building2 } from 'lucide-react';
import { api, type Me, type Restaurant, type Store } from '../lib/api';
import { signOut } from '../lib/auth';

/** Post-login: pick the restaurant, then the store. Members see their
 * memberships; supers browse the registry. */
export function ChooseScope() {
  const nav = useNavigate();
  const [restaurants, setRestaurants] = useState<Restaurant[] | null>(null);
  const [picked, setPicked] = useState<Restaurant | null>(null);
  const [stores, setStores] = useState<Store[] | null>(null);
  const [query, setQuery] = useState('');
  const [isSuper, setIsSuper] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const me = await api.get<Me>('/me');
        setIsSuper(me.isSuperAdmin);
        if (me.memberships.length > 0) {
          const rs = await Promise.all(
            me.memberships.map((m) => api.get<Restaurant>(`/restaurants/${m.restaurantId}`)),
          );
          setRestaurants(rs);
        } else if (me.isSuperAdmin) {
          setRestaurants(await api.get<Restaurant[]>('/restaurants', { limit: 20 }));
        } else {
          setRestaurants([]);
        }
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to load.');
      }
    })();
  }, []);

  async function search(q: string) {
    setQuery(q);
    if (isSuper) setRestaurants(await api.get<Restaurant[]>('/restaurants', { query: q, limit: 20 }));
  }

  async function pick(r: Restaurant) {
    setPicked(r);
    setStores(await api.get<Store[]>(`/restaurants/${r.restaurantId}/stores`));
  }

  return (
    <main style={{ maxWidth: 560, margin: '0 auto', padding: '72px 24px' }}>
      <div className="overline">{picked ? picked.name : 'Restoplus Admin'}</div>
      <h1 style={{ fontSize: 24, fontWeight: 800, letterSpacing: '-0.018em', margin: '4px 0 20px' }}>
        {picked ? 'Choose a store' : 'Choose a restaurant'}
      </h1>
      {error && <div className="err">{error}</div>}
      {/* V4-10: the platform console (provisioning lives there) gets a front
          door — supers otherwise had no way to find /platform. */}
      {!picked && isSuper && (
        <button className="console-card" onClick={() => nav('/platform')}>
          <span className="glyph"><Building2 size={17} /></span>
          <span>
            <b>Platform console</b>
            <span>Provision restaurants &amp; stores · live orders · printers · settings</span>
          </span>
          <span className="arrow">→</span>
        </button>
      )}
      {!picked && isSuper && (
        <input className="input" placeholder="Search restaurants…" value={query}
          onChange={(e) => void search(e.target.value)} style={{ marginBottom: 16 }} />
      )}
      {!picked && restaurants === null && <div className="loading">Loading…</div>}
      {!picked && restaurants?.length === 0 && (
        <p style={{ color: 'var(--ink-2)' }}>No restaurants for this account.</p>
      )}
      {!picked &&
        restaurants?.map((r) => (
          <div key={r.restaurantId} className="rowline" style={{ cursor: 'pointer' }} onClick={() => void pick(r)}>
            <div className="grow">
              <b>{r.name}</b>
              <span className="sub">{r.restaurantId} · {r.timezone}</span>
            </div>
            <span style={{ color: 'var(--ink-3)' }}>→</span>
          </div>
        ))}
      {picked && stores === null && <div className="loading">Loading stores…</div>}
      {picked &&
        stores?.map((s) => (
          <div key={s.storeId} className="rowline" style={{ cursor: 'pointer' }}
            onClick={() => nav(`/r/${picked.restaurantId}/s/${s.storeId}`)}>
            <div className="grow">
              <b>{s.name}</b>
              <span className="sub">
                {[s.pickupEnabled && 'pickup', s.deliveryEnabled && 'delivery', s.dineInEnabled && 'dine-in']
                  .filter(Boolean)
                  .join(' · ') || 'no channels enabled'}
              </span>
            </div>
            <span style={{ color: 'var(--ink-3)' }}>→</span>
          </div>
        ))}
      <div style={{ marginTop: 32, display: 'flex', gap: 12 }}>
        {picked && (
          <button className="btn btn-outline btn-sm" onClick={() => { setPicked(null); setStores(null); }}>
            ← Restaurants
          </button>
        )}
        <button className="btn btn-ghost btn-sm" onClick={() => { signOut(); nav('/login'); }}>
          Sign out
        </button>
      </div>
    </main>
  );
}
